<script setup lang="ts">
import { QuizType } from "@/enums/quiz.enums";
import { CourseContentActivityType, ComparisonId, CourseSubViewType, CourseType } from "~/enums/course.enums";

const { course, subView, claimCertificate } = useCourseState();

withDefaults(defineProps<{
  allChaptersDefaultExpanded?: boolean;
}>(), {
  allChaptersDefaultExpanded: false,
});

const claimCertificateClicked = async (callback: () => void) => {
  await claimCertificate();
  callback();
};

const getActivityId = () => {
  const route = useRoute();
  const lastMatched = route.matched[route.matched.length - 1];

  if (lastMatched?.name === CourseContentActivityType.Comparison) {
    return ComparisonId.toString();
  }

  return (lastMatched?.name === CourseContentActivityType.Lesson ? route.params.lessonId : route.params.quizId) as string | undefined;
};

const getSectionIndex = (sectionType: QuizType.Regular | QuizType.Kickoff | QuizType.Mastery) => {
  const activityId = getActivityId();
  if (activityId) {
    const sectionIndex = course.value?.chapters?.[sectionType]?.findIndex(chapter =>
      chapter.contents.some(activity => activity.id === parseInt(activityId, 10)),
    );

    if (typeof sectionIndex === 'undefined') {
      return null;
    }

    return sectionIndex;
  }

  return null;
};

const activeKickOffSectionIndex = computed(() => getSectionIndex(QuizType.Kickoff));
const activeRegularSectionIndex = computed(() => getSectionIndex(QuizType.Regular));
const activeMasterySectionIndex = computed(() => getSectionIndex(QuizType.Mastery));
</script>

<template>
  <!-- pb-[105.5px] is fix the overlap created by the feedback button -->
  <div
    v-if="course"
    :class="{'pb-[105.5px]': subView !== CourseSubViewType.Course}"
  >
    <CourseClaimCertificate
      v-if="course && course.certificateStatus"
      :state="course.certificateStatus"
      @claim-certificate="claimCertificateClicked"
    />

    <template v-if="course.chapters.kickoff.length">
      <Divider
        align="left"
        type="solid"
        class="my-5"
        :pt="{
          content: {
            class: subView === CourseSubViewType.Course ? 'bg-white' : 'bg-neutral-10'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Kick-off</span>
      </Divider>

      <CoursePlaylistSection
        :chapters="course.chapters.kickoff"
        :active-index="activeKickOffSectionIndex"
        :all-chapters-default-expanded="allChaptersDefaultExpanded"
      />
    </template>

    <template v-if="course.chapters.regular.length">
      <Divider
        v-if="course.kickoffAndMasteryQuizzesEnabled"
        align="left"
        type="solid"
        class="my-5"
        :pt="{
          content: {
            class: subView === CourseSubViewType.Course ? 'bg-white' : 'bg-neutral-10'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{course.type === CourseType.Workshop ? 'Cases' : 'Lessons'}}</span>
      </Divider>

      <CoursePlaylistSection
        :chapters="course.chapters.regular"
        :active-index="activeRegularSectionIndex"
        :all-chapters-default-expanded="allChaptersDefaultExpanded"
      />
    </template>

    <template v-if="course.chapters.mastery.length">
      <Divider
        align="left"
        type="solid"
        class="my-5"
        :pt="{
          content: {
            class: subView === CourseSubViewType.Course ? 'bg-white' : 'bg-neutral-10'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Mastery</span>
      </Divider>

      <CoursePlaylistSection
        :chapters="course.chapters.mastery"
        :active-index="activeMasterySectionIndex"
        :all-chapters-default-expanded="allChaptersDefaultExpanded"
      />
    </template>
  </div>
</template>
